import { PersonaVoice } from "@/components/ui/persona-voice";
import { SeverityDot, severityTone } from "@/components/ui/severity-dot";
import {
  CitationText,
  type ResolvedCitationData,
} from "@/components/citations";
import { cn } from "@/lib/utils";
import type { UnifiedLiveFinding } from "./types";
import { SilentPresenceCue } from "./live-timeline-blocks";

export function TimelineStepRow({
  stepNumber,
  title,
  personaReasoning,
  grounding,
  findings,
  isActive,
  onSelect,
  citationsMap,
  openPanel,
  trailingSilentCue,
}: {
  stepNumber: number;
  title: string;
  personaReasoning: string | null;
  grounding: string | null;
  findings: UnifiedLiveFinding[];
  isActive: boolean;
  onSelect: () => void;
  citationsMap: Map<string, ResolvedCitationData>;
  openPanel: (sourceType: string, sourceId: string) => void;
  trailingSilentCue?: boolean;
}) {
  const highestSeverity = findings.length > 0 ? findings[0].severity : null;

  return (
    <li className="border-t border-border/60 pt-7 first:border-t-0 first:pt-0">
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          "group block w-full text-left transition-colors",
          isActive ? "text-foreground" : "text-foreground hover:text-primary",
        )}
        aria-current={isActive ? "step" : undefined}
      >
        <div className="flex items-baseline gap-3">
          <span
            className={cn(
              "w-6 shrink-0 text-sm font-medium tabular-nums",
              isActive ? "text-primary" : "text-muted-foreground",
            )}
          >
            {String(stepNumber).padStart(2, "0")}
          </span>
          <h3 className="text-base font-semibold flex-1">{title}</h3>
          {highestSeverity && (
            <span
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
              title={`${findings.length} finding(s)`}
            >
              <SeverityDot tone={severityTone(highestSeverity)} />
              <span className="tabular-nums">{findings.length}</span>
            </span>
          )}
        </div>
      </button>

      {personaReasoning && (
        <PersonaVoice size="base" className="mt-3 pl-9 leading-relaxed">
          <CitationText
            text={personaReasoning}
            citations={citationsMap}
            onOpenPanel={openPanel}
          />
        </PersonaVoice>
      )}

      {grounding && (
        <p className="mt-2 pl-9 text-sm text-muted-foreground">
          <CitationText
            text={grounding}
            citations={citationsMap}
            onOpenPanel={openPanel}
          />
        </p>
      )}

      {findings.length > 0 && (
        <ul className="mt-3 space-y-3 pl-9">
          {findings.map((f) => (
            <li key={f.id} className="flex items-baseline gap-3 text-sm">
              <span className="shrink-0 pt-[7px]">
                <SeverityDot tone={severityTone(f.severity)} />
              </span>
              <div className="flex-1 space-y-1">
                <p className="text-base text-foreground font-medium">
                  {f.title || f.description}
                </p>
                {f.uiFact && (
                  <p className="text-sm text-muted-foreground">{f.uiFact}</p>
                )}
                {f.recommendation && (
                  <p className="text-sm text-muted-foreground">
                    {f.recommendation}
                  </p>
                )}
                {f.isPreliminary && (
                  <p className="text-xs text-muted-foreground">
                    Preliminary · {f.severity} · final once the run syncs
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {trailingSilentCue && <SilentPresenceCue />}
    </li>
  );
}
